import React, { useMemo, useState } from 'react';
import { AppFormInput } from '../../components/common/appForms';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../components/common/DecoratedHeader/DecoratedHeader';
import Logo from '../../components/common/Logo/Logo';
import type {
  BodyMeasurementFieldKey,
  BodyMeasurementValues,
} from '../../types/bodyMeasurements';
import CheckoutContainer from '../Checkout/components/CheckoutContainer';
import pageStyles from '../Checkout/CheckoutPage.module.css';
import { BODY_MEASUREMENT_FIELDS } from './bodyMeasurements.constants';
import {
  createEmptyMeasurementValues,
  maskMeasurementField,
  validateMeasurementValues,
} from './bodyMeasurements.utils';
import styles from './BodyMeasurements.module.css';

const BodyMeasurementsPage: React.FC = () => {
  const [values, setValues] = useState<BodyMeasurementValues>(() => createEmptyMeasurementValues());
  const [errors, setErrors] = useState<Partial<Record<BodyMeasurementFieldKey, string>>>({});
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const hasDraft = useMemo(
    () => Object.values(values).some((value) => value.trim().length > 0),
    [values],
  );

  const updateField = (key: BodyMeasurementFieldKey, rawValue: string) => {
    setSuccessMessage(null);
    setValues((current) => ({
      ...current,
      [key]: maskMeasurementField(key, rawValue),
    }));
    setErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const resetDraft = () => {
    setValues(createEmptyMeasurementValues());
    setErrors({});
  };

  const handleCancel = () => {
    if (!hasDraft) {
      setSuccessMessage(null);
      return;
    }

    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    resetDraft();
    setShowCancelConfirm(false);
    setSuccessMessage(null);
  };

  const requestSave = () => {
    const nextErrors = validateMeasurementValues(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setShowSaveConfirm(true);
  };

  const handleSaveConfirm = () => {
    if (isSaving) {
      return;
    }

    const nextErrors = validateMeasurementValues(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setShowSaveConfirm(false);
      return;
    }

    setIsSaving(true);
    setShowSaveConfirm(false);
    resetDraft();
    setSuccessMessage('Medidas salvas com sucesso. Obrigada por registrar sua evolução!');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsSaving(false);
  };

  return (
    <CheckoutContainer>
      <div className={styles.logoHeader}>
        <Logo className={styles.formLogo} />
        <DecoratedHeader
          className={styles.formTitle}
          title="Suas medidas corporais"
        />
        <p className={pageStyles.headerSubtitle}>
          Registre seu peso e circunferências do mês. Assim o time acompanha sua evolução com clareza.
        </p>
        <p className={pageStyles.headerSubtitle}>
          Preencha com calma, confira os valores e salve quando estiver tudo certo.
        </p>
      </div>

      {successMessage && (
        <div className={styles.feedbackWrap}>
          <AlertBanner variant="success" message={successMessage} />
        </div>
      )}

      <div className={`${styles.centeredLayout} ${styles.listWithFixedSend}`}>
        <article className={`${pageStyles.checkoutCard} ${styles.centeredCard} ${styles.flatCard}`}>
          <section className={styles.section} aria-labelledby="body-measurements-title">
            <h2 id="body-measurements-title" className={styles.sectionTitle}>
              Medidas corporais
            </h2>
            <p className={styles.sectionHint}>
              Informe os valores atuais. Use a vírgula para casas decimais.
            </p>
            <div className={styles.fieldsGrid}>
              {BODY_MEASUREMENT_FIELDS.map((field) => (
                <div key={field.key} className={styles.fieldItem}>
                  <AppFormInput
                    id={`body-measure-${field.key}`}
                    label={`${field.label} (${field.unit})`}
                    inputMode="decimal"
                    placeholder={field.placeholder}
                    value={values[field.key]}
                    error={errors[field.key]}
                    required
                    disabled={isSaving}
                    onChange={(event) => updateField(field.key, event.target.value)}
                  />
                </div>
              ))}
            </div>
          </section>
        </article>
      </div>

      <div className={`${pageStyles.footerFixed} ${styles.sendFooter}`}>
        <div className={styles.sendFooterInner}>
          <Button
            type="button"
            variant="outline"
            size="app"
            fullWidth
            disabled={isSaving}
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            size="app"
            fullWidth
            iconRight="arrow_forward"
            disabled={isSaving}
            onClick={requestSave}
          >
            Salvar medidas
          </Button>
        </div>
      </div>

      <ConfirmModal
        open={showCancelConfirm}
        title="Cancelar envio"
        message="Deseja descartar as medidas preenchidas?"
        confirmLabel="Descartar"
        cancelLabel="Voltar"
        variant="danger"
        onCancel={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelConfirm}
      />

      <ConfirmModal
        open={showSaveConfirm}
        title="Salvar medidas"
        message="Deseja mesmo salvar suas medidas?"
        confirmLabel="Salvar medidas"
        cancelLabel="Revisar"
        variant="info"
        icon="send"
        loading={isSaving}
        onCancel={() => {
          if (!isSaving) {
            setShowSaveConfirm(false);
          }
        }}
        onConfirm={handleSaveConfirm}
      />
    </CheckoutContainer>
  );
};

export default BodyMeasurementsPage;
